/**
 * Brand identity — the ONE place the product's name and canonical hosts live.
 *
 * Rebrand (2026-09-10): BuildForce → Wrenk. Everything user-facing should read
 * from here so the next rename is a one-file change, and so a partial cutover
 * (new name live, old hosts still serving) stays coherent.
 *
 * What deliberately does NOT change with this file:
 *  - MCP worker hostnames (`*-mcp.buildforce.io`, `mcp.buildforce.io`) — customers'
 *    connectors point at them; they move only once the new zone has DNS + routes.
 *  - The `bfio` Salesforce managed-package namespace — namespaces are permanent.
 *  - Inbound mailboxes (@buildforce.io) — until mail is DELIVERED for wrenk.ai.
 *    The SENDING domain has already moved to notify.wrenk.ai; see below.
 *  - Identifiers, DB values, env var names, route paths, cookie/storage keys.
 */

/** Display name, title case. Logos may set it in caps; prose never does. */
export const BRAND_NAME = "Wrenk";

/** The name we are moving away from — used only for "formerly" copy and redirects. */
export const LEGACY_BRAND_NAME = "BuildForce";

/** Legal entity for copyright lines. */
export const LEGAL_ENTITY = "SparkSMB LLC";

/** Canonical marketing/app host. Override per environment with NEXT_PUBLIC_SITE_URL. */
export const SITE_URL: string =
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.wrenk.ai";

export const SITE_HOST = new URL(SITE_URL).host;

/** Hosts that should 301 to SITE_URL once the new zone is live. */
export const LEGACY_HOSTS = [
  "buildforce.io",
  "www.buildforce.io",
  "wrenk.io",
  "www.wrenk.io",
] as const;

/**
 * Whether the middleware should redirect LEGACY_HOSTS to SITE_URL. Ships OFF so
 * the code can deploy before DNS for wrenk.ai exists; flip with
 * NEXT_PUBLIC_LEGACY_HOST_REDIRECT=true after the new domain serves in Vercel.
 */
export const LEGACY_HOST_REDIRECT_ENABLED =
  process.env.NEXT_PUBLIC_LEGACY_HOST_REDIRECT === "true";

/** The MCP connector endpoint customers paste into Claude/ChatGPT. Unchanged for now. */
export const MCP_CONNECTOR_URL = "https://mcp.buildforce.io/mcp";

/**
 * Mailboxes — addresses a HUMAN reads. These stay on the legacy domain until
 * mail addressed to wrenk.ai is actually delivered somewhere (Google Workspace
 * alias domain or Cloudflare Email Routing); a support address that bounces is
 * worse than an old one that works. Sending-domain verification is a separate,
 * already-satisfied condition — do not conflate the two (see below).
 */
export const SUPPORT_EMAIL = "support@buildforce.io";
export const HELLO_EMAIL = "hello@buildforce.io";
export const PRIVACY_EMAIL = "privacy@buildforce.io";
export const LEGAL_EMAIL = "legal@buildforce.io";
export const SECURITY_EMAIL = "security@buildforce.io";
export const PARTNERS_EMAIL = "partners@buildforce.io";

/**
 * SENDING domain — distinct from the mailboxes above, and it moves on a
 * different schedule. A sending domain only has to be verified in Resend
 * (DKIM + SPF + MX); a mailbox has to actually RECEIVE mail somewhere. So the
 * From: addresses below can live on wrenk.ai while every Reply-To above stays
 * on buildforce.io — a reply that bounces is worse than an old-looking sender.
 *
 * `notify.wrenk.ai` was verified in Resend on 2026-09-11 and production's
 * `RESEND_FROM_ADDRESS` already points at it, so these defaults describe what
 * production actually does rather than the domain it used to use. Every sender
 * still reads the env var first; these are the fallbacks.
 *
 * NEVER default a sender to a bare apex (`alerts@buildforce.io`) — only the
 * `notify.*` subdomain is a verified sending domain, so an apex From is
 * rejected by Resend outright.
 */
export const MAIL_SENDING_DOMAIN = "notify.wrenk.ai";

/** Transactional default (invites, drips, lifecycle). */
export const DEFAULT_FROM_ADDRESS = `invite@${MAIL_SENDING_DOMAIN}`;
/** Operational alerts (health checks, deployments). */
export const ALERTS_FROM_ADDRESS = `alerts@${MAIL_SENDING_DOMAIN}`;
/** Human-sounding sender for lifecycle mail; pair with a real Reply-To. */
export const TEAM_FROM_ADDRESS = `hello@${MAIL_SENDING_DOMAIN}`;

/**
 * Social / entity links for JSON-LD sameAs and the footer. These are the
 * profiles that actually exist today (still under the legacy handle); update
 * when the new profiles are created.
 */
/**
 * The booking link used by marketing pages and every transactional email.
 *
 * The previous value — `cal.com/timowens-buildforce/discovery` — was hardcoded
 * in two places and **404s**: the Cal.com username does not exist. It was
 * going out in live lifecycle emails, so leads clicked through to a Cal.com
 * "this page does not exist". Verified working in a browser 2026-09-11; note
 * that curl is useless for checking a Cal link (Cal serves 404 to non-JS
 * clients, including for its own demo links), so check these in a browser.
 *
 * `NEXT_PUBLIC_CALCOM_URL` already carries the /demo embed's slug; this is the
 * absolute form for contexts that need a full URL (emails, JSON-LD, docs).
 */
export const BOOKING_URL = "https://cal.com/tim-owens/30min";

export const SOCIAL_LINKS = {
  linkedin: "https://www.linkedin.com/company/buildforce-io",
  x: "https://x.com/buildforce_io",
  github: "https://github.com/buildforce-io",
} as const;

export const absoluteUrl = (path = "/"): string =>
  new URL(path, SITE_URL).toString();
